// @ts-nocheck
// =============================================================================
// File: frontend/src/components/usePushNotifications.js
// React Hook for Web Push Notifications
// =============================================================================
//
// USAGE:
//   import { usePushNotifications } from './components/usePushNotifications';
//   const { subscribe, unsubscribe, isSubscribed, permission } = usePushNotifications(fetchWithAuth, API_BASE);
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { isIOS, isAndroid } from '../utils/platformDetect';

/**
 * Convert VAPID public key from base64url to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Detect device type and browser
 */
function getDeviceInfo() {
  const ua = navigator.userAgent;
  let deviceType = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
  let browser = 'Unknown';
  
  // Browser
  if (/Chrome/.test(ua) && !/Edg|OPR/.test(ua)) {
    browser = 'Chrome';
  } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
  } else if (/Firefox/.test(ua)) {
    browser = 'Firefox';
  } else if (/Edg/.test(ua)) {
    browser = 'Edge';
  } else if (/OPR/.test(ua)) {
    browser = 'Opera';
  }
  
  // Friendly device name
  const deviceName = `${browser} on ${
    deviceType === 'ios' ? 'iPhone/iPad' :
    deviceType === 'android' ? 'Android' :
    deviceType === 'mobile' ? 'Mobile' : 'Desktop'
  }`;
  
  return { deviceType, browser, deviceName };
}

/**
 * Check if running as installed PWA
 */
function isPWAInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

/**
 * Custom hook for Web Push Notifications
 */
export function usePushNotifications(fetchWithAuth, API_BASE) {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);
  
  // Initialize on mount
  useEffect(() => {
    initializePush();
  }, []);
  
  /**
   * Initialize push notification support
   */
  const initializePush = async () => {
    try {
      // Check browser support
      const supported = 
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;
      
      setIsSupported(supported);
      
      if (!supported) {
        setLoading(false);
        return;
      }
      
      // Get current permission
      setPermission(Notification.permission);
      
      // Register service worker
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;
      setRegistration(reg);
      
      // Check existing subscription
      const existingSub = await reg.pushManager.getSubscription();
      if (existingSub) {
        setSubscription(existingSub);
        setIsSubscribed(true);
      }
      
      // Fetch VAPID public key from backend
      try {
        const response = await fetch(`${API_BASE}/api/push/vapid-public-key`);
        if (response.ok) {
          const data = await response.json();
          setVapidPublicKey(data.publicKey);
        }
      } catch (e) {
        console.warn('Could not fetch VAPID key:', e);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Push init error:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration) {
      throw new Error('Push notifications not supported');
    }
    
    if (!vapidPublicKey) {
      throw new Error('VAPID key not available');
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        throw new Error('Notification permission denied');
      }
      
      // Subscribe to push
      const pushSub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      setSubscription(pushSub);
      
      // Get device info
      const deviceInfo = getDeviceInfo();
      
      // Send to backend
      const response = await fetchWithAuth(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        body: JSON.stringify({
          endpoint: pushSub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(pushSub.getKey('auth'))))
          },
          device_type: deviceInfo.deviceType,
          browser: deviceInfo.browser,
          device_name: deviceInfo.deviceName
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }
      
      setIsSubscribed(true);
      setLoading(false);
      
      return pushSub;
    } catch (err) {
      console.error('Subscribe error:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [isSupported, registration, vapidPublicKey, fetchWithAuth, API_BASE]);
  
  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Unsubscribe from browser
      await subscription.unsubscribe();
      
      // Remove from backend
      await fetchWithAuth(`${API_BASE}/api/push/unsubscribe`, {
        method: 'POST',
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
      
      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [subscription, fetchWithAuth, API_BASE]);
  
  /**
   * Send test notification
   */
  const sendTest = useCallback(async () => {
    if (!isSubscribed) {
      throw new Error('Not subscribed');
    }
    
    const response = await fetchWithAuth(`${API_BASE}/api/push/test`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('Failed to send test');
    }
    
    return response.json();
  }, [isSubscribed, fetchWithAuth, API_BASE]);
  
  /**
   * Show local notification (via service worker)
   */
  const showLocalNotification = useCallback((title, options = {}) => {
    if (!registration) return;
    
    registration.showNotification(title, {
      icon: '/icons/icon-192.png',
      badge: '/icons/badge-72.png',
      ...options
    });
  }, [registration]);
  
  return {
    // State
    isSupported,
    permission,
    isSubscribed,
    subscription,
    loading,
    error,
    isPWA: isPWAInstalled(),
    
    // Actions
    subscribe,
    unsubscribe,
    sendTest,
    showLocalNotification,
    refresh: initializePush
  };
}

export default usePushNotifications;